import React, { useState } from 'react';
import { Calendar, Trash2, MessageCircleQuestion } from 'lucide-mui';
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { RootState } from '../../../reducers';
import { getInitialState } from '../../app/appSlice';
import {
  disconnectGoogleCalendarIntegration,
  initGoogleCalendarIntegration,
} from '../../../services/integrationService';
import useLimits from '../../plan/hooks/limits';

const GoogleMeetIntegration = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => ({
    user: state?.settings.user,
  }));

  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);

  const {
    isActive,
    personEmail,
    personName,
    upcomingEvent,
  } = user.integrations.google.calendar;

  const guardLimits = useLimits();

  const handleConnectGoogleCalendar = async () => {
    guardLimits('googleMeetBot', async () => {
      const response = await initGoogleCalendarIntegration();
      window.location.href = response.oauthUrl;
    });
  };

  const handleDisconnectGoogleCalendar = async () => {
    setIsDisconnectDialogOpen(false);
    await disconnectGoogleCalendarIntegration();
    dispatch(getInitialState());
  };

  if (!isActive) {
    return (
      <Card>
        <CardContent>
          <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
            <Calendar sx={{ width: 40, height: 40, color: 'primary.main' }} />
            <Typography variant="h6">Connect Google Calendar</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Allow access to your calendar so we can automatically join and
              record your meetings
            </Typography>
            <Button
              onClick={handleConnectGoogleCalendar}
              variant="contained"
              startIcon={<Calendar />}
              data-testid="connect-google-calendar-button"
            >
              Connect with Google
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card
        elevation={0}
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={3}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ width: '100%', justifyContent: 'space-between' }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Calendar
                    sx={{
                      width: 40,
                      height: 40,
                      color: 'primary.main',
                    }}
                  />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {personName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {personEmail}
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  onClick={() => setIsDisconnectDialogOpen(true)}
                  variant="contained"
                  color="error"
                  startIcon={<Trash2 />}
                  size="small"
                  sx={{
                    minWidth: { xs: '100%', sm: 'auto' },
                    mt: { xs: 1, sm: 0 },
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                    '&:hover': {
                      bgcolor: 'error.dark',
                      '& .MuiSvgIcon-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  Disconnect
                </Button>
              </Stack>
            </Box>

            <Box
              sx={{
                p: 2,
                bgcolor: 'primary.lighter',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'primary.light',
              }}
            >
              <Stack direction="row" spacing={2.5} alignItems="flex-start">
                <MessageCircleQuestion
                  sx={{
                    width: 40,
                    height: 40,
                    color: 'primary.main',
                    flexShrink: 0,
                  }}
                />
                <Stack spacing={1}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'primary.dark', fontWeight: 600 }}
                  >
                    Meeting Bot Instructions
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'primary.dark',
                      lineHeight: 1.5,
                      opacity: 0.9,
                    }}
                  >
                    When your next meeting starts, look for &quot;{user.name}
                    &apos;s Notetaker&quot; in the waiting room and click
                    &quot;Admit&quot; to begin recording
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 'medium',
                  mb: 2,
                }}
              >
                Upcoming Meeting
              </Typography>

              {upcomingEvent?.eventId ? (
                <TableContainer
                  sx={{
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    '& .MuiTableCell-head': {
                      bgcolor: 'background.default',
                      fontWeight: 'medium',
                      display: { xs: 'none', sm: 'table-cell' },
                      px: 2,
                      py: 1.5,
                    },
                    '& .MuiTableCell-body': {
                      display: { xs: 'block', sm: 'table-cell' },
                      border: { xs: 0, sm: 'inherit' },
                      px: 2,
                      py: { xs: 1, sm: 1.5 },
                      '&:first-of-type': {
                        pt: { xs: 2, sm: 1.5 },
                      },
                      '&:last-child': {
                        pb: { xs: 2, sm: 1.5 },
                      },
                    },
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Meeting</TableCell>
                        <TableCell>Start Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        sx={{
                          '&:last-child td': {
                            borderBottom: 0,
                          },
                          flexDirection: { xs: 'column', sm: 'row' },
                        }}
                      >
                        <TableCell sx={{ maxWidth: { xs: '100%', sm: 300 } }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: { xs: 'block', sm: 'none' },
                              mb: 0.5,
                            }}
                          >
                            Meeting
                          </Typography>
                          <Typography noWrap>
                            {upcomingEvent.eventName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: { xs: 'block', sm: 'none' },
                              mb: 0.5,
                            }}
                          >
                            Start Time
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {moment
                              .utc(upcomingEvent.startTime)
                              .local()
                              .format('h:mma MMM DD YYYY')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    bgcolor: 'background.default',
                    borderRadius: 2,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography color="text.secondary">
                    No upcoming meetings found
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={isDisconnectDialogOpen}
        onClose={() => setIsDisconnectDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Disconnect Google Calendar?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will stop automatic meeting recordings. You can reconnect your
            calendar at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setIsDisconnectDialogOpen(false)}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDisconnectGoogleCalendar}
            variant="contained"
            color="error"
            sx={{ minWidth: 100 }}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GoogleMeetIntegration;
